import { action, computed, makeObservable } from "mobx";
import type { UserReaction } from "../../activities/ActivityModel";
import { REACTIONS, type Reaction } from "../../activities/types";
import type { Api } from "../../core/apis";
import { BaseModel } from "../../core/BaseModel";
import type { Stores } from "../../core/stores";
import { Permission } from "../../permissions/types";
import type { IconProp } from "../../ui/icons";
import type {
  BaseFeedModelData,
  BusinessFeedMention,
} from "../feed/BusinessFeedItem";
import { buildFeedMentionsFromText } from "../feed/utils";
import type { BusinessStaticUserInfoWithUserUid } from "../plan/BusinessPlanModel";

export interface BusinessFeedCommentReplyData extends BaseFeedModelData {
  businessPlanUid: string;
  feedItemUid: string;
  feedCommentUid: string;
  authorUserUid: string;
  reactions: UserReaction[];
  mentions: BusinessFeedMention[];
  mentionUids: string[]; // for efficient query

  text: string;
}

export class BusinessFeedCommentReplyModel extends BaseModel<BusinessFeedCommentReplyData> {
  constructor(stores: Stores, api: Api, data: BusinessFeedCommentReplyData) {
    super(stores, api, data);

    makeObservable(this);

    Object.assign(this.data, {
      mentions: this.data.mentions ?? [],
      mentionUids: this.data.mentionUids ?? [],

      hidden: this.data.hidden ?? false,
      deleted: this.data.deleted ?? false,
      edited: this.data.edited ?? false,
      reported: this.data.reported ?? 0,
    });
  }

  @computed get comment() {
    const c = this.stores.businessFeedCommentsStore.byId.get(
      this.data.feedCommentUid,
    );

    if (!c) {
      throw Error("Comment not found");
    }

    return c;
  }

  @computed get author(): BusinessStaticUserInfoWithUserUid | undefined {
    const i =
      this.comment.feedItem.businessPlan?.data.userInfo[
        this.data.authorUserUid
      ];

    if (i) {
      return { ...i, userUid: this.data.authorUserUid };
    }

    return undefined;
  }

  @computed get maybeAuthor(): BusinessStaticUserInfoWithUserUid | undefined {
    const authorUid = this.data.authorUserUid;
    if (authorUid) {
      const u = this.comment.feedItem.businessPlan?.data.userInfo[authorUid];

      if (u) {
        return { ...u, userUid: authorUid };
      }
    }
    return undefined;
  }

  @computed get isAuthor(): boolean {
    const author = this.maybeAuthor;
    const user = this.comment.feedItem.businessPlan?.userInPlan;

    return !!user && !!author && author.userUid === user.uid;
  }

  @computed get isAdmin(): boolean {
    return this.comment.feedItem.businessPlan.checkPermission(
      Permission.CanManageFeedItems,
    );
  }

  @action async toggleReaction(reaction: Reaction) {
    const feedItem = this.comment.feedItem;
    const currentUserUid = feedItem?.businessPlan?.userInPlan?.data.uid;

    if (!currentUserUid || !feedItem) {
      return undefined;
    }

    const hasReacted = this.data.reactions.findIndex(
      (r) => r.reaction === reaction && r.userUid === currentUserUid,
    );

    if (hasReacted !== -1) {
      this.data.reactions.splice(hasReacted, 1);
    } else {
      this.data.reactions.push({
        userUid: currentUserUid,
        reaction,
      });
    }

    await this.api.businessFeedCommentsReplyApi.update(this.data.uid, {
      reactions: this.data.reactions,
    });

    void this.comment.updateFeedReplyCommentInCache(this);
  }

  @computed get reactionRowItemCounts(): {
    icon: IconProp | string;
    count: number;
  }[] {
    const anyReaction: { icon: IconProp | string; count: number }[] = [];
    REACTIONS.forEach((r) => {
      const c = this.data.reactions.filter(
        (rr) => rr.reaction === r.type,
      ).length;
      if (c) {
        anyReaction.push({
          icon: r.icon,
          count: c,
        });
      }
    });
    return anyReaction;
  }

  @action async edit(text: string) {
    this.data.text = text;

    const { mentions, mentionUids } = buildFeedMentionsFromText(
      text,
      this.comment.feedItem.businessPlan,
    );
    this.data.mentions = mentions;
    this.data.mentionUids = mentionUids;

    await this.api.businessFeedCommentsReplyApi.set(this);

    return this.saveEditAction("edited");
  }

  @action async saveEditAction(
    option: "edited" | "deleted" | "hidden" | "reported",
  ) {
    const businessPlan = this.comment.feedItem.businessPlan;

    if (!businessPlan) {
      return undefined;
    }

    if (option === "edited") this.data.edited = true;
    if (option === "deleted") this.data.deleted = true;
    if (option === "hidden") this.data.hidden = !this.data.hidden;
    if (option === "reported") this.data.reported++;

    await this.api.businessFeedCommentsReplyApi.update(this.data.uid, {
      edited: this.data.edited,
      deleted: this.data.deleted,
      hidden: this.data.hidden,
      reported: this.data.reported,
    });

    void this.comment.updateFeedReplyCommentInCache(this);
  }

  toFireStore(): Omit<BusinessFeedCommentReplyData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
